import { JTAC, AIRFIELD } from '../core/SimConfig.js';
import { radioClipUrl } from '../audio/soundManifest.js';
import { onRunway, runwayPoint } from '../training/runwayFrame.js';
import { rand } from '../utils.js';

// Hold-short point in world XZ, resolved once off the runway frame. (Imported
// from training/ only because that is where the runway-frame helpers live; the
// module is pure geometry with no training coupling.)
const HOLD_SHORT = runwayPoint(JTAC.atc.holdShort.along, JTAC.atc.holdShort.lateral);

// Which net a cue belongs to. Omitted means the controller's, so only the
// handful of tower cues have to say.
const netOf = (cue) => cue.net || 'jtac';

/**
 * The tower script: ordered, one-shot, and each step owns *both* halves of its
 * timing — `ready` is the moment it goes out, `expired` the moment it is retired
 * unsent. That pair is what lets one script cover every spawn pose without the
 * scenarios knowing anything about it:
 *
 *   • parked, cold    — nothing is ready until the start sequence finishes, then
 *                       all three play out in order as the player flies them.
 *   • parked, running — taxi clearance lands a few seconds in (`openingDelay`).
 *   • on the runway   — the taxi step is already expired, so the script opens on
 *                       takeoff clearance instead.
 *   • airborne        — both ground steps are expired; only the handoff plays.
 *
 * Steps read the radio's own latched world flags rather than the aircraft
 * directly, so each predicate is a field test and the sampling happens once.
 */
const TOWER = [
  {
    cue: 'taxiClearance',
    // Spooled up and still on the ramp. `isRunning` is the *end* of the 44 s
    // start sequence, which is exactly "the engines are up".
    ready: (r) => !!r.aircraft.engineState?.isRunning && r._grounded && !r._onRunway,
    expired: (r) => r._airborne || r._onRunway,
  },
  {
    cue: 'takeoffClearance',
    // Holding short, or already lined up — either way the next thing that
    // happens is a takeoff, and a player who taxis straight on without stopping
    // still gets cleared.
    ready: (r) => r._grounded && (r._holdingShort || r._onRunway),
    expired: (r) => r._airborne,
  },
  {
    cue: 'contactJtac',
    // Combat only: the handoff, a beat after getting airborne. Expired outright
    // in Free Flight, where there is no controller to hand off to.
    ready: (r) => r._airTime >= JTAC.atc.handoffDelay,
    expired: (r) => !r._jtacEnabled,
  },
];

/**
 * The mission radio: field tower and ground controller, sharing one voice.
 *
 * A net that narrates the player's sortie with no line of script anywhere in the
 * scenario definitions. Two nets ride it, and which of them is live is the whole
 * of the per-scenario configuration:
 *
 *   • **Tower** talks to anyone who is not in flight school — taxi clearance,
 *     takeoff clearance, and the handoff (see {@link TOWER}). Free Flight gets
 *     the first two; Combat gets all three.
 *   • **The JTAC** is Combat only, and is held off the air entirely until the
 *     tower's handoff call has finished and `atc.jtacDelay` has run out. So the
 *     opening of a combat mission flown from the ramp is taxi, takeoff, target
 *     brief, *then* a controller — in that order, with nobody talking over
 *     anybody.
 *
 * Underneath, two halves, kept apart below:
 *
 *   • **The director** watches the world and *requests cues* — "cleared hot",
 *     "good hits", "still taking fire". It reads nothing but state the sim
 *     already keeps (the enemy list, the munition counters, the aircraft), polled
 *     every {@link JTAC.sampleInterval} rather than hooked into the systems it
 *     observes — so adding a cue costs a few lines here and nothing anywhere
 *     else. One sample is O(enemies), the same budget the pilot's head scan
 *     runs on.
 *   • **The radio** decides what actually goes out. Cues are *requests*, not
 *     commands: they queue by priority with a time-to-live, wait for dead air,
 *     and are dropped if the moment passes before the net is free — which is
 *     what keeps a flurry of events from becoming a flurry of talking.
 *
 * ── Why it sounds like a radio and not a soundboard ────────────────────────
 * Four rules do most of that work:
 *
 *   1. **One voice, with dead air.** A transmission runs to its end and is
 *      followed by a randomized gap. Only a decisively more urgent cue
 *      (`preemptMargin`) ever cuts one off, so clearance to attack can step on
 *      idle chatter but two tactical calls never overlap.
 *   2. **Late is worse than silent.** A queued cue carries a TTL: "cleared hot"
 *      that could not get out while the player was shooting is binned, not
 *      transmitted after the pass.
 *   3. **Nothing twice in a row.** Selection is per-cue but the no-repeat guard
 *      is global (`recentMemory`), because one recording serves more than one
 *      cue — and a cue with nothing fresh left to say stays quiet rather than
 *      repeating itself.
 *   4. **Lines wear out.** `maxPlays` retires the ingress script (smoke, target
 *      designation) after a couple of engagements, so the fifth pass is just
 *      "cleared hot" and not the full three-beat briefing again.
 *
 * Audio is streamed through plain HTMLAudioElements, one per clip (see
 * `radioClipUrl` in audio/soundManifest.js for why), and the transmission clock
 * runs on sim time — so it freezes with the pause like the instructor VO, and a
 * clip the browser silently fails to load can never jam the net.
 */
export default class JTACRadio {
  /**
   * @param {object} deps
   * @param {import('../aircraft/Aircraft.js').default} deps.aircraft
   * @param {import('../enemy/EnemyManager.js').default} deps.enemyManager
   * @param {import('../weapons/EnemyMissileSystem.js').default} [deps.enemyMissileSystem]
   * @param {import('./MissionStats.js').default} [deps.stats] - the run's stats
   *   accumulator, read (never written) for the munition counters.
   * @param {boolean} [deps.jtac] - is there a ground controller on this mission?
   *   Combat only. False leaves the tower script running on its own, minus the
   *   handoff, and never fetches a single JTAC clip.
   * @param {number} [deps.masterVolume]
   * @param {number} [deps.jtacVolume]
   */
  constructor({
    aircraft, enemyManager, enemyMissileSystem = null, stats = null,
    jtac = false, masterVolume = 1, jtacVolume = 1,
  }) {
    this.aircraft = aircraft;
    this.enemyManager = enemyManager;
    this.enemyMissileSystem = enemyMissileSystem;
    this.stats = stats;

    // ── Radio ──
    this._voices = new Map();   // clip id → HTMLAudioElement
    this._audio = null;         // element currently transmitting, if any
    this._clipLength = 0;
    this._soundTime = 0;        // sim time the current transmission has run
    this._deadAir = JTAC.openingDelay;  // the net opens a few seconds into the run
    this._queue = [];           // pending cues, highest priority first
    this._current = null;       // cue being transmitted (for preemption tests)
    this._recent = [];          // clip ids, most recent first
    this._cues = new Map();     // cue name → { plays, cooldown }
    this._masterVolume = masterVolume;
    this._jtacVolume = jtacVolume;
    this._paused = false;
    this._standDown = false;
    this._closed = false;

    // ── Director ──
    this._accum = 0;
    // Is the controller's net live yet? Combat only, and not until the tower has
    // handed the flight over — everything on the `jtac` net is refused until it
    // opens (see _request / _updateHandoff).
    this._jtacEnabled = !!jtac;
    this._jtacOpen = false;
    this._handoffTimer = JTAC.atc.jtacDelay;
    this._workingTarget = false; // a tactical call has gone out (retires check-in)
    this._towerStep = 0;        // cursor into TOWER
    this._airborne = false;     // latched; retires the tower's ground steps
    this._airTime = 0;          // s since getting airborne (paces the handoff)
    this._missionTime = 0;      // s the controller has held the net (bounds check-in)
    this._checkInStep = 0;      // cursor into JTAC.checkIn.script
    this._eng = null;           // active engagement, see _updateEngagement
    this._run = null;           // gun run in progress, see _updateGunRun
    this._lastFired = this._gunFired();
    this._mavHits = this._mavHitCount();          // see _updateStrike
    this._mavResolved = this._mavResolvedCount();
    this._calm = 0;             // s since anything tense happened
    this._chatterTimer = rand(JTAC.chatter.interval.min, JTAC.chatter.interval.max);

    // Latest world sample.
    this._alive = 0;            // live JTAC-controlled targets anywhere
    this._alivePrev = 0;        // ... as of the previous sample (see _updateGunRun)
    this._groupAlive = 0;       // ... of those, the ones in the active engagement
    this._range = Infinity;     // to the nearest one
    this._nearX = 0;
    this._nearZ = 0;
    this._noseOn = -1;          // cos of the nose-to-nearest-target angle
    this._defensive = false;    // a missile is chasing the player
    this._grounded = false;     // resting on the ground (tower geometry)
    this._onRunway = false;     // on the paved runway
    this._holdingShort = false; // at the hold-short point

    if (JTAC.enabled) this._preload();
  }

  update(dt) {
    if (!JTAC.enabled || this._closed) return;

    // Transmission + dead-air clock: per frame, so the gaps stay tight.
    this._tickVoice(dt);

    this._accum += dt;
    if (this._accum < JTAC.sampleInterval) return;
    const sdt = this._accum;
    this._accum = 0;

    if (this.aircraft?.destroyed) { this.close(); return; }

    this._sample();
    this._updateTower(sdt);
    this._updateHandoff(sdt);
    // Kept outside the gate so the round counter stays in step with the gun
    // across the handoff; it bails on its own while the net is closed.
    this._updateGunRun(sdt);
    // The rest are skipped wholesale rather than left to be refused per-request:
    // the engagement machine latches "smoke called, target assigned, cleared
    // hot" as the player closes, so running it against a closed net would mark
    // an ingress as briefed that the player never actually heard.
    if (this._jtacOpen) {
      this._updateCheckIn(sdt);
      this._updateStrike();
      this._updateEngagement();
      this._updateRecovery();
      this._updateChatter(sdt);
    }
    this._service(sdt);
  }

  // ════════════════════════════════════════════════════════════════════════
  //  Director — reads the world, requests cues
  // ════════════════════════════════════════════════════════════════════════

  /**
   * One pass over the enemy list for everything the cues need: how many
   * controlled targets are left, which is nearest, whether the nose is on it,
   * how much of the active engagement survives, and whether the player is
   * currently defending.
   *
   * "Controlled" is everything that is not a fast mover — armour, AA and
   * helicopters are a ground controller's business; a MiG is not, and air-to-air
   * deliberately gets no commentary from him at all.
   */
  _sample() {
    const ac = this.aircraft;
    const ents = this.enemyManager?.entities;
    const groupR2 = JTAC.engage.groupRadius * JTAC.engage.groupRadius;

    this._alivePrev = this._alive;
    this._alive = 0;
    this._groupAlive = 0;
    this._range = Infinity;
    let nx = 0;
    let ny = 0;
    let nz = 0;

    if (ents) {
      for (let i = 0; i < ents.length; i++) {
        const e = ents[i];
        if (e.isJet || e.destroyed || e.dead) continue;
        const pos = e.flight?.position || e.position;
        if (!pos) continue;
        this._alive++;

        if (this._eng) {
          const gx = pos.x - this._eng.cx;
          const gz = pos.z - this._eng.cz;
          if (gx * gx + gz * gz <= groupR2) this._groupAlive++;
        }

        const dx = pos.x - ac.position.x;
        const dy = pos.y - ac.position.y;
        const dz = pos.z - ac.position.z;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d < this._range) {
          this._range = d;
          this._nearX = pos.x;
          this._nearZ = pos.z;
          nx = dx;
          ny = dy;
          nz = dz;
        }
      }
    }

    if (this._range < Infinity && this._range > 1) {
      const fwd = ac.forwardVector;
      this._noseOn = (nx * fwd.x + ny * fwd.y + nz * fwd.z) / this._range;
    } else {
      this._noseOn = -1;
    }

    // Tower geometry. `isGrounded` rather than the raw contact flag, which
    // chatters once the airframe settles (see Aircraft.isGrounded); "airborne"
    // is latched above a little altitude so a bounce on the takeoff roll cannot
    // un-retire the ground steps, and never clears, so landing later in the
    // flight does not re-run the script.
    const atc = JTAC.atc;
    this._grounded = ac.isGrounded;
    if (!this._airborne && !this._grounded && ac.altitudeAGL > atc.airborneAlt) {
      this._airborne = true;
    }
    this._onRunway = onRunway(ac.position.x, ac.position.z, atc.runwayMargin);
    const hx = ac.position.x - HOLD_SHORT.x;
    const hz = ac.position.z - HOLD_SHORT.z;
    this._holdingShort = hx * hx + hz * hz <= atc.holdShort.radius * atc.holdShort.radius;

    // Anything actually chasing the player. The net goes quiet while it is.
    this._defensive = false;
    const missiles = this.enemyMissileSystem?.activeMissiles;
    if (missiles) {
      for (let i = 0; i < missiles.length; i++) {
        if (missiles[i].target === ac) { this._defensive = true; break; }
      }
    }
  }

  /**
   * Walk the tower script (see {@link TOWER}), one step at a time and in order.
   *
   * A step is left alone until it either goes out or expires, and is re-requested
   * every sample until it does — so a busy net delays a clearance rather than
   * losing it, exactly as the check-in script below works. The cursor advances on
   * the cue having *transmitted*, not on having been requested; `maxPlays: 1` on
   * each is what makes `plays` the sequence cursor.
   */
  _updateTower(dt) {
    if (this._airborne) this._airTime += dt;
    while (this._towerStep < TOWER.length) {
      const step = TOWER[this._towerStep];
      if (this._cueState(step.cue).plays > 0) { this._towerStep++; continue; }
      if (step.expired(this)) { this._towerStep++; continue; }
      if (step.ready(this)) this._request(step.cue);
      return;
    }
  }

  /**
   * Open the controller's net, once.
   *
   * Nothing on the `jtac` net is allowed out until the tower has actually said
   * the handoff, and then not until that call has finished and `jtacDelay` has
   * run out — the timer only runs in the clear, so a transmission still going
   * out holds it. That delay is the whole reason the opening of a combat mission
   * reads as a sequence instead of a pile-up.
   */
  _updateHandoff(dt) {
    if (!this._jtacEnabled || this._jtacOpen) return;
    if (this._cueState('contactJtac').plays === 0) return;
    if (this._isSounding()) return;
    this._handoffTimer -= dt;
    if (this._handoffTimer <= 0) this._jtacOpen = true;
  }

  /**
   * The opening exchange, walked one beat at a time (`JTAC.checkIn.script`): the
   * next line is not requested until the previous has actually transmitted, so a
   * tactical call cutting in pauses the script rather than scrambling it. Each
   * beat is re-requested every sample until it goes out, which is what lets one
   * sit through a busy net without needing a TTL of its own.
   *
   * Anything still unsent once `window` has elapsed is abandoned. The script is
   * scene-setting — it carries nothing the player still needs — and a check-in
   * delivered deep into his first engagement is worse than no check-in at all.
   * The window runs from the moment the controller took the net, not from the
   * start of the mission: a cold start and a taxi can put minutes between those
   * two, which would otherwise expire the whole exchange before it ever opened.
   * That lateness is also why the remainder is dropped the instant a tactical
   * call goes out (`yieldPriority`) and not only on the clock: a player handed
   * over already within range of a target would otherwise hear "cleared for
   * combat, guns cold" *after* being cleared hot on it.
   */
  _updateCheckIn(dt) {
    const cfg = JTAC.checkIn;
    this._missionTime += dt;
    if (this._checkInStep >= cfg.script.length) return;
    if (this._missionTime > cfg.window || this._workingTarget) {
      this._checkInStep = cfg.script.length;
      return;
    }
    const name = cfg.script[this._checkInStep];
    // Advance on the beat having transmitted, not on having been requested:
    // `maxPlays: 1` on each is what makes `plays` the sequence cursor.
    if (this._cueState(name).plays > 0) { this._checkInStep++; return; }
    this._request(name);
  }

  /**
   * One engagement is one cluster of targets worked over one or more passes: it
   * opens when the player closes on a live target, carries the ingress script as
   * he presses in, and resolves when the group is dead or he leaves.
   *
   * The group is pinned to the position the engagement opened at rather than
   * re-clustered every sample — a point-distance test survives its members
   * dying, and it is what makes "leaving" mean something once the one target the
   * player was closest to is a wreck.
   */
  _updateEngagement() {
    const cfg = JTAC.engage;
    const ac = this.aircraft;

    if (this._eng) {
      const dx = ac.position.x - this._eng.cx;
      const dz = ac.position.z - this._eng.cz;
      if (this._groupAlive === 0) {
        this._eng = null;             // group is gone — nothing left to control here
      } else if (Math.hypot(dx, dz) > cfg.closeRange) {
        // Egressed with survivors on the deck: the next task, or hold.
        if (this._eng.worked) this._request('reattack');
        this._eng = null;
      }
    }

    if (!this._eng) {
      if (this._range <= cfg.openRange) {
        this._eng = { cx: this._nearX, cz: this._nearZ, assigned: false, hot: false, worked: false };
        this._request('mark');        // smoke on the target
      }
      return;
    }

    if (this._noseOn < cfg.nosedOn) return;
    if (!this._eng.assigned && this._range <= cfg.assignRange) {
      this._eng.assigned = true;
      this._request('assign');
    }
    if (!this._eng.hot && this._range <= cfg.clearedHotRange) {
      this._eng.hot = true;
      this._request('clearedHot');
    }
  }

  /**
   * Bracket a gun run and call its result.
   *
   * A run is delimited by rounds *leaving the gun* (the stats counter), not by
   * the trigger flag — sampling at 4 Hz would otherwise miss a short burst
   * entirely. What the run achieved picks the call: dead targets get "good
   * hits", rounds on target that did not finish the job get "still taking
   * fire", and a clean miss gets re-tasked onto the target. Pressing in below
   * `dangerCloseAlt` adds the friendlies check as a second beat — which is why
   * that cue carries a long TTL: it has to wait out the call it answers.
   */
  _updateGunRun(dt) {
    const cfg = JTAC.gunRun;
    const fired = this._gunFired();
    const prev = this._lastFired;
    this._lastFired = fired;
    const shooting = fired > prev;
    // Counter tracked either way, so the first sample after the handoff sees a
    // delta of zero rather than every round fired before the net opened.
    if (!this._jtacOpen) { this._run = null; return; }

    if (shooting && !this._run) {
      // The survivor count is taken from the *previous* sample, not this one:
      // the trigger is only noticed up to a sample late, and a target that died
      // in that window died to this run.
      this._run = {
        fired0: prev, hits0: this._gunHits(), alive0: this._alivePrev,
        idle: 0, low: false, warned: false,
      };
    }
    if (!this._run) return;

    this._run.idle = shooting ? 0 : this._run.idle + dt;
    if (this.aircraft.altitudeAGL < cfg.dangerCloseAlt) {
      this._run.low = true;
      // The caution is only worth anything with the trigger still down, so it
      // goes out mid-run (once) rather than waiting for the result call. Its
      // short TTL bins it if the net stays busy for the rest of the pass.
      if (!this._run.warned && shooting) {
        this._run.warned = true;
        this._request('dangerCloseWarn');
      }
    }
    if (this._run.idle < cfg.endDelay) return;

    const run = this._run;
    this._run = null;
    if (fired - run.fired0 < cfg.minRounds) return;   // a twitch, not a run

    const hits = this._gunHits() - run.hits0;
    const killed = run.alive0 - this._alive;
    if (this._eng) this._eng.worked = true;
    // Last target on the deck just died: the mission is over and the victory
    // theme is about to come up. Nothing to say, and nothing left to re-attack.
    if (this._alive === 0) return;

    if (killed > 0 && hits > 0) this._request('goodHits', 'gunClips');
    else if (hits > 0) this._request('stillHot');
    else this._request('reattack');

    if (run.low && hits > 0) this._request('dangerClose');
  }

  /**
   * Call the result of a Maverick impact.
   *
   * Nothing to bracket, unlike a gun run: a Maverick resolves once, seconds
   * after launch and one shot at a time, and the stats accumulator already
   * scores it as a hit or a miss when it does — so a rise in the resolved count
   * *is* the event, and the 4 Hz sample cannot miss it the way it would miss a
   * short burst. Kill credit reads the previous sample's survivor count for the
   * same reason the gun run does: the impact is noticed up to a sample late, and
   * a target that died in that window died to this missile.
   */
  _updateStrike() {
    const hits = this._mavHitCount();
    const resolved = this._mavResolvedCount();
    const hit = hits > this._mavHits;
    const landed = resolved > this._mavResolved;
    this._mavHits = hits;
    this._mavResolved = resolved;
    if (!landed) return;

    if (this._eng) this._eng.worked = true;
    // Last target on the deck just died: the victory theme owns the moment and
    // there is nothing left to re-attack (see _updateGunRun).
    if (this._alive === 0) return;

    const killed = this._alivePrev - this._alive;
    if (hit && killed > 0) this._request('goodHits');
    else if (hit) this._request('stillHot');
    else this._request('reattack');
  }

  /** The player is bringing it home: gear down, low, and over the field. */
  _updateRecovery() {
    const cfg = JTAC.rtb;
    const ac = this.aircraft;
    if (ac.gear < cfg.gearDown || ac.altitudeAGL > cfg.altitude) return;
    const dx = ac.position.x - AIRFIELD.position.x;
    const dz = ac.position.z - AIRFIELD.position.z;
    if (dx * dx + dz * dz > cfg.range * cfg.range) return;
    this._request('rtb');
  }

  /**
   * Chatter fills the lulls and nothing else. It needs a stretch of genuine
   * quiet behind it (`calmTime`) — no shooting, nobody defending, no attack run
   * developing — and its own interval is re-rolled after *every* transmission,
   * so it spaces itself off the tactical calls too instead of crowding in behind
   * one.
   */
  _updateChatter(dt) {
    const cfg = JTAC.chatter;
    const tense = this._defensive
      || this._run !== null
      || (this._eng !== null && this._range <= JTAC.engage.clearedHotRange);
    this._calm = tense ? 0 : this._calm + dt;
    this._chatterTimer -= dt;

    if (this._calm < cfg.calmTime || this._chatterTimer > 0) return;
    if (this._queue.length) return;        // something real is already waiting
    this._request('chatter');
  }

  // 30mm counters, read off the run's stats accumulator. Absent (or frozen, as
  // it is once the mission completes) simply means no gun runs are detected.
  _gunFired() { return this.stats?.gunFired ?? 0; }
  _gunHits()  { return this.stats?.gunHits ?? 0; }

  // Maverick results. The resolved count (hits + misses) is what moves on an
  // impact; `mavericksFired` moves at launch and is deliberately not read —
  // there is nothing to say about a missile still in the air.
  _mavHitCount() { return this.stats?.maverickHits ?? 0; }

  _mavResolvedCount() {
    const st = this.stats;
    return st ? st.maverickHits + st.maverickMisses : 0;
  }

  // ════════════════════════════════════════════════════════════════════════
  //  Radio — decides what actually goes out
  // ════════════════════════════════════════════════════════════════════════

  /**
   * Ask for a cue. Honoured only if it is off cooldown and has plays left;
   * re-requesting one already waiting refreshes its TTL rather than stacking a
   * duplicate behind it.
   *
   * `variant` names an alternative clip pool on the cue (`gunClips` and the
   * like): the same call phrased for a different weapon, under one priority and
   * one cooldown, so an event is still called exactly once but in the language
   * of whatever fired. The pool is resolved here and carried on the queue entry,
   * so a re-request cannot swap the wording out from under a waiting beat.
   */
  _request(name, variant = null) {
    if (this._standDown) return;
    const cue = JTAC.cues[name];
    if (!cue) return;
    // The controller is not on the air until the tower has handed over. Enforced
    // here, at the one chokepoint every call goes through, so the ordering holds
    // however a cue comes to be asked for.
    if (netOf(cue) === 'jtac' && !this._jtacOpen) return;
    const st = this._cueState(name);
    if (st.cooldown > 0) return;
    if (cue.maxPlays && st.plays >= cue.maxPlays) return;

    const pending = this._queue.find((q) => q.name === name);
    if (pending) { pending.ttl = cue.ttl ?? JTAC.queueTTL; return; }

    const clips = (variant && cue[variant]) || cue.clips;
    this._queue.push({ name, cue, clips, net: netOf(cue), ttl: cue.ttl ?? JTAC.queueTTL });
    this._queue.sort((a, b) => b.cue.priority - a.cue.priority);
    // Bounded: a backlog of tactical calls is a worse soundscape than a dropped
    // one, and the lowest-priority entry is the one worth least.
    if (this._queue.length > JTAC.maxQueue) this._queue.length = JTAC.maxQueue;
  }

  /** Age the cooldowns and the queue, then transmit if the net is free. */
  _service(dt) {
    for (const st of this._cues.values()) st.cooldown = Math.max(0, st.cooldown - dt);
    for (let i = this._queue.length - 1; i >= 0; i--) {
      this._queue[i].ttl -= dt;
      if (this._queue[i].ttl <= 0) this._queue.splice(i, 1);   // the moment has passed
    }
    if (!this._queue.length) return;
    // Radio silence while the player is defending — he has a launch warning in
    // his ears and nothing the JTAC has to say beats it.
    if (this._defensive) return;

    if (this._isSounding()) {
      const margin = this._queue[0].cue.priority - (this._current?.priority ?? 0);
      if (margin < JTAC.preemptMargin) return;
    } else if (this._deadAir > 0) {
      return;
    }
    this._transmit(this._queue[0]);
  }

  _transmit(entry) {
    const clip = this._pick(entry.clips);
    this._queue.splice(this._queue.indexOf(entry), 1);
    // Nothing he has not just said — better off the net than repeating himself.
    // Costs the cue nothing: no play spent, no cooldown started.
    if (!clip) return;

    const st = this._cueState(entry.name);
    st.plays++;
    st.cooldown = entry.cue.cooldown ?? 0;
    this._current = entry.cue;
    // Introductions are over once he has started working a target.
    if (entry.net === 'jtac' && entry.cue.priority >= JTAC.checkIn.yieldPriority) {
      this._workingTarget = true;
    }

    this._recent.unshift(clip);
    if (this._recent.length > JTAC.recentMemory) this._recent.length = JTAC.recentMemory;
    // Every transmission pushes the next bit of chatter out, so an idle line
    // never lands on the heels of a tactical call.
    this._chatterTimer = rand(JTAC.chatter.interval.min, JTAC.chatter.interval.max);

    this._play(clip, entry.net);
  }

  /**
   * Pick the recording for a cue: anything it has not said lately, at random.
   * The memory is global rather than per-cue because one recording can serve
   * several cues ("put guns on that target" is both a designation and a
   * re-attack), and a verbatim repeat is what gives a soundboard away. Falling
   * back to "anything but the last thing said" keeps a two-clip cue usable; a
   * single-clip cue that was just played returns null and is simply skipped.
   */
  _pick(clips) {
    if (!clips || !clips.length) return null;
    let pool = clips.filter((c) => !this._recent.includes(c));
    if (!pool.length) pool = clips.filter((c) => c !== this._recent[0]);
    if (!pool.length) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  _cueState(name) {
    let st = this._cues.get(name);
    if (!st) {
      st = { plays: 0, cooldown: 0 };
      this._cues.set(name, st);
    }
    return st;
  }

  // ── Playback ──────────────────────────────────────────────────────────────

  _play(clip, net) {
    const el = this._voice(clip, net);
    if (!el) return;
    this._stopVoice();
    this._audio = el;
    this._soundTime = 0;
    this._clipLength = el.duration > 0 ? el.duration : JTAC.assumedLength;
    el.volume = this._volume();
    try { el.currentTime = 0; } catch { /* not yet seekable */ }
    if (!this._paused) el.play().catch(() => { /* autoplay gate — the net stays quiet */ });

    if (!(el.duration > 0)) {
      el.addEventListener('loadedmetadata', () => {
        if (this._audio === el && el.duration > 0) this._clipLength = el.duration;
      }, { once: true });
    }
  }

  /**
   * Transmission clock. Run on sim time against the clip's known length rather
   * than off the element's `ended` event, for the same reason the instructor's
   * captions are: a file that fails to load, stalls, or is quietly dropped by
   * the browser would otherwise hold the net open forever.
   */
  _tickVoice(dt) {
    if (this._audio) {
      this._soundTime += dt;
      if (this._soundTime < this._clipLength) return;
      this._stopVoice();
      this._current = null;
      this._deadAir = rand(JTAC.gap.min, JTAC.gap.max);
    } else if (this._deadAir > 0) {
      this._deadAir = Math.max(0, this._deadAir - dt);
    }
  }

  _isSounding() { return this._audio !== null; }

  _stopVoice() {
    if (this._audio) {
      this._audio.pause();
      try { this._audio.currentTime = 0; } catch { /* not yet seekable */ }
    }
    this._audio = null;
  }

  // One element per clip, created on demand and kept for the life of the
  // mission. Only one transmission ever sounds, so a clip that plays twice just
  // rewinds.
  _voice(clip, net) {
    const url = radioClipUrl(clip, net);
    if (!url) return null;
    let el = this._voices.get(url);
    if (!el) {
      el = new Audio(url);
      el.preload = 'auto';
      this._voices.set(url, el);
    }
    return el;
  }

  // Warm every clip the cue table can reach, so the first call does not wait on
  // a fetch. `openingDelay` covers the rest of that gap. Walks every clip-list
  // field rather than just `clips`, so a cue's variant pools (see `_request`)
  // are warmed too; `_voice` caches by id, so an id in two pools costs one
  // element and one fetch.
  _preload() {
    for (const cue of Object.values(JTAC.cues)) {
      const net = netOf(cue);
      // Free Flight never hears the controller, so it never fetches him either
      // — three tower clips instead of the whole JTAC library.
      if (net === 'jtac' && !this._jtacEnabled) continue;
      for (const field of Object.values(cue)) {
        if (!Array.isArray(field)) continue;
        for (const clip of field) this._voice(clip, net)?.load();
      }
    }
  }

  _volume() {
    return Math.max(0, Math.min(1, JTAC.volume * this._jtacVolume * this._masterVolume));
  }

  // ── External control ──────────────────────────────────────────────────────

  /**
   * Freeze/resume with the sim. The director and the transmission clock both
   * ride sim time and so stop on their own; the element is real-time and has to
   * be told (idempotent — App syncs this every frame).
   */
  setPaused(paused) {
    if (paused === this._paused) return;
    this._paused = paused;
    if (!this._audio) return;
    if (paused) this._audio.pause();
    else this._audio.play().catch(() => {});
  }

  /** User master-volume change from the settings overlay. */
  setMasterVolume(v) {
    this._masterVolume = Math.max(0, Math.min(1, v));
    if (this._audio) this._audio.volume = this._volume();
  }

  /** User JTAC-volume change from the settings overlay. */
  setJtacVolume(v) {
    this._jtacVolume = Math.max(0, Math.min(1, v));
    if (this._audio) this._audio.volume = this._volume();
  }

  /**
   * Nothing further to say: bin the backlog and request nothing more, but let a
   * call already going out finish. Used the moment the last target dies, where
   * the few seconds before the victory beat belong to the explosion rather than
   * the net — and where `silence()` would cut the controller off mid-word on the
   * one call of the mission worth hearing to the end.
   */
  standDown() {
    this._standDown = true;
    this._queue.length = 0;
  }

  /** Cut the current transmission and bin the backlog, but stay on the air. */
  silence() {
    this._queue.length = 0;
    this._current = null;
    this._stopVoice();
  }

  /** Off the air for good — the mission is over, one way or the other. */
  close() {
    this.silence();
    this._closed = true;
  }

  dispose() {
    this.close();
    for (const el of this._voices.values()) el.src = '';
    this._voices.clear();
  }
}
